import './svg-js-extension';

import {
  ArrayXY,
  Circle,
  G,
  Image,
  Line,
  NumberAlias,
  Pattern,
  Point,
  Polygon,
  Rect,
  Shape,
  StrokeData,
  Svg,
  Element as SvgElement,
  Text,
} from '@svgdotjs/svg.js';
import _ from 'lodash';

import { Container, ForeignObject, Path, Polyline, SVG } from '@svgdotjs/svg.js';
import { ClampJoint } from '../../user/cutting-settings.model';
import { TextureSettings } from '../cutting-base-material.model';
import { FastenerBasePointEnum } from '../cutting-custom-hole.model';
import { GrooveTypeEnum } from '../cutting-panel-base-slot.model';
import { AngleMode, Cutout, CutoutPoint, CutoutTypeEnum, PlateTypeEnum } from '../cutting-panel-outline.model';
import {
  Countertop,
  CountertopLine,
  CountertopLinesJoint,
  CountertopLinesJointTypeEnum,
  CountertopPanel,
  SlotAppearanceOverride,
} from './countertop.interface';
import { PanelLengthChangeDirection } from './countertop.utils';
import {
  Chamfer,
  Chamfer2SlotParams,
  CountertopEdgeBanding,
  CountertopFastenersSet,
  CountertopHole,
  CountertopSlot,
  Cut,
  CutInside,
  CutInsideWithAngle,
  FilletSlotParams,
  InPlateSlotParams,
  InSideSlotParams,
  RabbetSlotParams,
  Rounding,
  RoundingInside,
} from './outline';
import {
  CountertopAngleProcessingBase,
  RoundingBase,
  RoundingInsideBase,
} from './outline/countertop-angle-processing.interface';
import { ShapeAppearance } from './svg-js-extension';

export const lineAppearance: ShapeAppearance = {
  stroke: { color: 'black', width: 5 },
  fill: { color: 'white' },
} as const;
export const lineSeparatorStroke: StrokeData = { color: 'grey', width: 4, dasharray: '8' } as const;

export const holeAppearance: { [key: string]: ShapeAppearance } = {
  through: {
    fill: { color: 'white' },
    stroke: { color: 'black', width: 1 },
  },
  blind: {
    fill: { color: '#782a06' },
    stroke: { color: '#782a06', width: 2 },
  },
  side: {
    fill: { color: '#843916' },
    stroke: { color: '#843916', width: 2 },
  },
} as const;

export const sizeAppearance: ShapeAppearance = {
  fill: {
    color: 'blue',
  },
  stroke: {
    width: 3,
    color: 'blue',
  },
} as const;

export const clampAppearance: ShapeAppearance = {
  fill: {
    color: 'rgba(0 0 0 / .08)',
  },
  stroke: {
    width: 3,
    color: 'grey',
  },
} as const;

export const slotAppearance: (slot: CountertopSlot) => ShapeAppearance = (slot: CountertopSlot) => ({
  fill: { color: slot.plate === PlateTypeEnum.Back ? '#80808090' : '#00800090' },
  stroke: {
    color: 'black',
    width: 1,
    dasharray: slot.type === GrooveTypeEnum.InSide ? '7' : undefined,
  },
});

export const selectorAppearance: {
  highlighter: (active: boolean) => ShapeAppearance;
  selector: ShapeAppearance;
} = {
  selector: { fill: { color: 'none' }, stroke: { color: 'transparent', width: lineAppearance.stroke.width * 10 } },
  highlighter: (active: boolean) => ({
    fill: { color: 'none' },
    stroke: {
      color: active ? 'red' : 'transparent',
      width: active ? lineAppearance.stroke.width * 2 : lineAppearance.stroke.width,
    },
  }),
} as const;

export interface LineRenderOptions {}

/**
 *
 * @param lines
 * @param options
 */
export function renderLines(lines: CountertopLine[], options?: Partial<LineRenderOptions>): G[] {
  return lines.map(l => renderLine(l, options));
}

/**
 *
 * @param line
 * @param options
 */
export function renderLine(line: CountertopLine, options?: Partial<LineRenderOptions>): G {
  const group = new G();

  group.attr({ lineId: line.uid });

  group.rect(line.length, line.width).appearance(lineAppearance).nonScalingStroke().attr({ type: 'line' });

  let panelHeights = 0;
  _.initial(line.panels).forEach(p => {
    const x = (panelHeights += p.length);
    group.line([x, 0, x, p.width]).stroke(lineSeparatorStroke).nonScalingStroke().attr({ type: 'separator' });
  });

  return group;
}

/**
 *
 * @param cutouts
 * @param countertopLength
 * @param countertopWidth
 */
export async function renderCutouts(
  cutouts: Cutout[],
  countertopLength: number,
  countertopWidth: number
): Promise<G[]> {
  return Promise.all(cutouts.map(async c => await renderCutout(c, countertopLength, countertopWidth)));
}

/**
 *
 * @param cutout
 * @param countertopLength
 * @param countertopWidth
 */
export async function renderCutout(cutout: Cutout, countertopLength: number, countertopWidth: number): Promise<G> {
  const group = new G();

  const r =
    cutout.type === CutoutTypeEnum.Rectangle
      ? 0
      : cutout.type === CutoutTypeEnum.RoundedRectangle
      ? cutout.radius
      : 10000;

  const xOffset =
    cutout.basePoint === CutoutPoint.TopRight || cutout.basePoint === CutoutPoint.BottomRight
      ? -cutout.height
      : cutout.basePoint === CutoutPoint.Center
      ? -cutout.height / 2
      : cutout.basePoint === CutoutPoint.Custom
      ? -cutout.fragmentParams.x
      : 0;

  const yOffset =
    cutout.basePoint === CutoutPoint.BottomLeft || cutout.basePoint === CutoutPoint.BottomRight
      ? -cutout.width
      : cutout.basePoint === CutoutPoint.Center
      ? -cutout.width / 2
      : cutout.basePoint === CutoutPoint.Custom
      ? -(cutout.width - cutout.fragmentParams.y)
      : 0;

  const x =
    cutout.angle === CutoutPoint.TopRight || cutout.angle === CutoutPoint.BottomRight
      ? countertopLength - cutout.x
      : cutout.x;
  const y =
    cutout.angle === CutoutPoint.BottomLeft || cutout.angle === CutoutPoint.BottomRight
      ? countertopWidth - cutout.y
      : cutout.y;

  let cutoutEl: Shape | ForeignObject;
  if (cutout.type === CutoutTypeEnum.Fragment) {
    const svgText = await fetch(`/api-cutting/standard-cutouts/image/${cutout.fragmentImageS3Key}`).then(response =>
      response.text()
    );

    // Т к в svgText могут быть и комментарии, то приходится делать через #document
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, 'image/svg+xml');
    const svgEl = doc.getElementsByTagName('svg')[0];

    const svgStyle = document.createElementNS('http://www.w3.org/2000/svg', 'style');
    svgStyle.textContent = `
      svg.lkbsdoigmlwprom * {
        stroke-width: 8px;
      }`;
    svgEl.appendChild(svgStyle);
    svgEl.classList.add('lkbsdoigmlwprom');

    const tmpContainer = document.createElement('div');
    tmpContainer.appendChild(svgEl);
    const validSvgText = tmpContainer.innerHTML;

    const svgElement = SVG(validSvgText);

    svgElement.width(cutout.height);
    svgElement.height(cutout.width);

    cutoutEl = new ForeignObject().add(svgElement).size(cutout.height, cutout.width);

    group.add(cutoutEl);

    cutoutEl.x(x);
    cutoutEl.y(y);
  } else {
    cutoutEl = group.rect(cutout.height, cutout.width).radius(r).x(x).y(y).appearance(lineAppearance);
  }

  cutoutEl.dmove(xOffset, yOffset);

  if (cutout.rotateDeg % 360) {
    // Пользователь ожидает направление вращения как в стандартной системе координат, когда y+ идет вверх, а здесь y+ идет вниз
    // => надо инвертировать направление вращения, чтобы положительный угол вращал фигуру против часовой стрелки
    const rotateCoefficient = -1;

    cutoutEl.rotate(cutout.rotateDeg * rotateCoefficient, x, y);
  }

  group
    .circle(lineAppearance.stroke.width * 3)
    .center(x, y)
    .stroke('none')
    .fill('red')
    .nonScalingStroke();

  return group;
}

/**
 *
 * @param getLengthBySide
 * @param appearanceOverride
 */
export function renderSlots(
  slots: { [key: string]: CountertopSlot[] },
  getLengthBySide: (side: string) => number,
  appearanceOverride?: SlotAppearanceOverride[]
): G[] {
  return _(slots)
    .map((val, sideN) => {
      if (val?.length) {
        const group = new G().attr({ slotSide: sideN });

        val.forEach(s => renderSlot(s, getLengthBySide(sideN), appearanceOverride).addTo(group));

        return group;
      }
      return null;
    })
    .compact()
    .value();
}

/**
 *
 * @param slot
 * @param length
 * @param appearanceOverride
 */
export function renderSlot(slot: CountertopSlot, length: number, appearanceOverride?: SlotAppearanceOverride[]): G {
  let slotWidth = 0;
  let offset = 0;

  switch (slot.type) {
    case GrooveTypeEnum.InPlate:
      slotWidth = (slot.parameters as InPlateSlotParams).h;
      offset = (slot.parameters as InPlateSlotParams).dx;
      break;

    case GrooveTypeEnum.InSide:
      slotWidth = (slot.parameters as InSideSlotParams).g;
      break;

    case GrooveTypeEnum.Rabbet:
      slotWidth = (slot.parameters as RabbetSlotParams).h;
      break;

    case GrooveTypeEnum.Chamfer2:
      slotWidth = (slot.parameters as Chamfer2SlotParams).b;
      break;

    case GrooveTypeEnum.Fillet:
      slotWidth = (slot.parameters as FilletSlotParams).r;
      break;

    case GrooveTypeEnum.Chamfer:
      slotWidth = 16;
      break;
  }

  const group = new G().attr({ slotName: slot.name });

  const getAppearance = () => {
    const override = _.findLast(appearanceOverride, o => o.slotId === slot.id);

    return override ? override.appearance(slot) : slotAppearance(slot);
  };

  group.rect(length, slotWidth).y(offset).appearance(getAppearance()).nonScalingStroke();

  return group;
}

/**
 *
 * @param fastenersSet
 * @param countertopLength
 * @param countertopWidth
 */
export function renderFastenersSets(
  fastenersSet: CountertopFastenersSet[],
  countertopLength: number,
  countertopWidth: number
): G[] {
  return fastenersSet.map(s => renderFastenersSet(s, countertopLength, countertopWidth));
}

/**
 *
 * @param fastenerSet
 * @param countertopLength
 * @param countertopWidth
 */
export function renderFastenersSet(
  fastenerSet: CountertopFastenersSet,
  countertopLength: number,
  countertopWidth: number
): G {
  const group = new G();

  const holes = fastenerSet.holes.map(h => ({ ...h, position: fastenerSet.position }));
  renderHoles(holes, 0, 0).forEach(h => h.addTo(group));

  fastenersSetPositioningByBasePoint(group, fastenerSet, countertopLength, countertopWidth);

  return group;
}

/**
 *
 * @param sideN
 * @param length
 * @param onClick
 */
export function renderSideSelector(sideN: number, length: number, onClick: (sideN: number) => void) {
  const group = new G().attr({ type: 'sideSelector', sideName: sideN });

  const highlight = renderSideHighlight(length, 'transparent');
  const selector = highlight
    .clone()
    .addClass('cursor-pointer')
    .appearance(selectorAppearance.selector)
    .mouseover(function () {
      highlight.stroke(selectorAppearance.highlighter(true).stroke);
    })
    .mouseout(function () {
      highlight.stroke(selectorAppearance.highlighter(false).stroke);
    })
    .click(function () {
      onClick(sideN);
    });

  group.add(highlight);
  group.add(selector);

  return group;
}

/**
 *
 * @param length
 * @param color
 */
export function renderSideHighlight(length: number, color = 'black'): Line {
  return new Line().plot(0, 0, length, 0).stroke({ width: 5, color: color }).nonScalingStroke();
}

/**
 *
 * @param angleProcessing
 * @param defaultSize
 * @param color
 */
export function renderAngleHighlight(
  angleProcessing: CountertopAngleProcessingBase,
  defaultSize = 0,
  color = 'black'
): Shape | null {
  let highlighter: Shape = null;

  switch (angleProcessing?.mode) {
    case AngleMode.Rounded:
      highlighter = getAngleRoundingContour((angleProcessing.parameters as RoundingBase).r);
      break;

    case AngleMode.RoundedInside:
      highlighter = getAngleInsideRoundingContour((angleProcessing.parameters as RoundingInsideBase).r);
      break;

    case AngleMode.CuttedInside:
      {
        if (!(angleProcessing.parameters instanceof CutInside)) {
          break;
        }

        const points = {
          zeroPoint1: new Point(0, angleProcessing.parameters.y),
          zeroPoint2: new Point(angleProcessing.parameters.x, 0),
          roundingPoint1: new Point(
            angleProcessing.parameters.x - angleProcessing.parameters.r,
            angleProcessing.parameters.y
          ),
          roundingPoint2: new Point(
            angleProcessing.parameters.x,
            angleProcessing.parameters.y - angleProcessing.parameters.r
          ),
          angleSize: 90,
          angleRadius: angleProcessing.parameters.r,
          anglePoint: new Point(angleProcessing.parameters.x, angleProcessing.parameters.y),
        };

        highlighter = getAngleInsideCuttingContour(
          points.zeroPoint1,
          points.zeroPoint2,
          points.roundingPoint1,
          points.roundingPoint2,
          points.anglePoint,
          points.angleRadius
        );
      }

      break;

    case AngleMode.CuttedInsideWithAngle:
      {
        const points = getAngleInsideCuttingWithAnglePoints(angleProcessing);

        highlighter = getAngleInsideCuttingContour(
          points.zeroPoint1,
          points.zeroPoint2,
          points.roundingPoint1,
          points.roundingPoint2,
          points.anglePoint,
          points.angleRadius
        );
      }

      break;

    case AngleMode.Chamfer:
    case AngleMode.Cutted:
      let xCut = 0;
      let yCut = 0;

      if (angleProcessing.parameters instanceof Chamfer) {
        xCut = yCut = angleProcessing.parameters.length;
      }

      if (angleProcessing.parameters instanceof Cut) {
        xCut = angleProcessing.parameters.x;
        yCut = angleProcessing.parameters.y;
      }

      highlighter = getAngleCutContour(xCut, yCut);
      break;

    default:
      highlighter = defaultSize
        ? new Polyline().plot([0, defaultSize, 0, 0, defaultSize, 0]).fill('none').nonScalingStroke()
        : null;
      break;
  }

  highlighter.appearance({
    ...lineAppearance,
    stroke: { ...lineAppearance.stroke, color },
    fill: { ...lineAppearance.fill, color: 'none' },
  });

  return highlighter;
}

/**
 *
 * @param box
 * @param fastenerSet
 * @param countertopLength
 * @param countertopWidth
 */
function fastenersSetPositioningByBasePoint(
  box: G,
  fastenerSet: CountertopFastenersSet,
  countertopLength: number,
  countertopWidth: number
): void {
  const x =
    fastenerSet.position === 'rc' ||
    (fastenerSet.position === 'tc' && fastenerSet.basePoint === FastenerBasePointEnum.End) ||
    (fastenerSet.position === 'bc' && fastenerSet.basePoint === FastenerBasePointEnum.Start)
      ? countertopLength - fastenerSet.x
      : fastenerSet.x;
  const y =
    fastenerSet.position === 'bc' ||
    (fastenerSet.position === 'rc' && fastenerSet.basePoint === FastenerBasePointEnum.End) ||
    (fastenerSet.position === 'lc' && fastenerSet.basePoint === FastenerBasePointEnum.Start)
      ? countertopWidth - fastenerSet.y
      : fastenerSet.y;

  box.dmove(x, y);

  if (
    ((fastenerSet.position === 'lc' || fastenerSet.position === 'rc') &&
      fastenerSet.basePoint === FastenerBasePointEnum.End) ||
    ((fastenerSet.position === 'tc' || fastenerSet.position === 'bc') &&
      fastenerSet.basePoint === FastenerBasePointEnum.Start)
  ) {
    box.flip('y', y);
  }

  if (fastenerSet.position === 'tc' || fastenerSet.position === 'bc') {
    box.rotate(90, x, y);
  }
}

/**
 *
 * @param holes
 * @param countertopLength
 * @param countertopWidth
 */
export function renderHoles(
  holes: CountertopHole[],
  countertopLength: number,
  countertopWidth: number
): (Circle | Line)[] {
  return holes.map(h => renderHole(h, countertopLength, countertopWidth));
}

/**
 *
 * @param hole
 * @param countertopLength
 * @param countertopWidth
 */
export function renderHole(hole: CountertopHole, countertopLength: number, countertopWidth: number): Circle | Line {
  let shape: Line | Circle;

  if (hole.type === PlateTypeEnum.Edge) {
    const holeRenderDiameter = _.clamp(hole.diameter, 1, 5);

    shape = new Line().plot(0, 0, hole.depth, 0).stroke({ ...holeAppearance.side.stroke, width: holeRenderDiameter });
  } else {
    shape = new Circle().size(hole.diameter).appearance(holeAppearance.blind);
  }

  shape.dmove(hole.x, hole.y).nonScalingStroke();

  holePositioningByBasePoint(shape, hole, countertopLength, countertopWidth);

  return shape;
}

/**
 *
 * @param shape
 * @param hole
 * @param countertopLength
 * @param countertopWidth
 */
function holePositioningByBasePoint(
  shape: Line | Circle,
  hole: CountertopHole,
  countertopLength: number,
  countertopWidth: number
): void {
  if (
    hole.position === 'rc' ||
    (hole.position === 'tc' && hole.basePoint === FastenerBasePointEnum.End) ||
    (hole.position === 'bc' && hole.basePoint === FastenerBasePointEnum.Start)
  ) {
    shape.flip('x', countertopLength / 2);
  }

  if (
    hole.position === 'bc' ||
    (hole.position === 'rc' && hole.basePoint === FastenerBasePointEnum.End) ||
    (hole.position === 'lc' && hole.basePoint === FastenerBasePointEnum.Start)
  ) {
    shape.flip('y', countertopWidth / 2);
  }
}

/**
 *
 */
export function renderAnglesProcessing(anglesProcessing: { [key: string]: CountertopAngleProcessingBase }): G[] {
  return _(anglesProcessing)
    .values()
    .map(p => {
      if (p && p.mode !== AngleMode.None) {
        const group = renderAngleProcessing(p);

        return group;
      }
      return null;
    })
    .compact()
    .value();
}

/**
 *
 * @param angleProcessing
 */
export function renderAngleProcessing(angleProcessing: CountertopAngleProcessingBase): G {
  switch (angleProcessing.mode) {
    case AngleMode.Rounded:
      return renderAngleRounding(angleProcessing);

    case AngleMode.RoundedInside:
      return renderAngleInsideRounding(angleProcessing);

    case AngleMode.CuttedInside:
      return renderAngleInsideCutting(angleProcessing);

    case AngleMode.CuttedInsideWithAngle:
      return renderAngleInsideCuttingWithAngle(angleProcessing);

    case AngleMode.Chamfer:
    case AngleMode.Cutted:
      return renderAngleCutted(angleProcessing);

    default:
      throw new Error(`Unknown angle type ${angleProcessing.mode}`);
  }
}

/**
 *
 * @param n
 * @param length
 * @param edgeBanding
 * @param color
 */
export function renderEdgeBanding(
  n: number | string,
  length: number,
  edgeBanding: CountertopEdgeBanding,
  color?: string
): G {
  return createGroupWithElements([
    renderSideHighlight(length, color ?? 'orange').attr({ edgeBandingSide: n, edgeBandingName: edgeBanding.name }),
  ]);
}

/**
 *
 * @param angleProcessing
 * @param color
 */
export function renderAnglesEdgeBanding(angleProcessing: CountertopAngleProcessingBase, color?: string): G {
  const highlighter = renderAngleHighlight(angleProcessing, undefined, color ?? 'orange');

  const group = createGroupWithElements([highlighter]);

  const backdropRect = group
    .rect(+highlighter.width() * 2, +highlighter.height() * 2)
    .fill('none')
    .stroke({ color: 'transparent', width: 0.000001 })
    .center(0, 0);
  highlighter.move(backdropRect.cx(), backdropRect.cy());

  return group;
}

/**
 *
 * @param line
 */
export function renderPanelsJoints(line: CountertopLine): G {
  const group = new G().attr({ type: 'panelsJoints', lineId: line.uid });

  let panelHeights = 0;
  line.panelsJoints.forEach(pj => {
    if (!pj.connector) {
      return;
    }

    const x = (panelHeights += pj.panel1.length);
    group.add(
      renderPanelsConnector(pj.connector)
        .center(x, line.width / 2)
        .rotate(90)
    );
  });

  group.rect(line.length, line.width).move(0, 0).fill('none').stroke({ color: 'transparent', width: 0.00000001 });

  return group;
}

/**
 *
 * @param line
 * @param onClick
 */
export function renderPanelsJointsSelectors(line: CountertopLine, onClick?: (connector: ClampJoint) => void): G {
  const group = new G().attr({ type: 'panelsJointsSelectors', lineId: line.uid });

  let panelHeights = 0;
  line.panelsJoints.forEach(pj => {
    if (!pj.connector) {
      return;
    }

    const x = (panelHeights += pj.panel1.length);

    const highlighter = renderPanelsConnector(pj.connector)
      .center(x, line.width / 2)
      .rotate(90);

    highlighter.children()?.forEach(j => (j as Shape).appearance(selectorAppearance.highlighter(false)));

    const selector = highlighter.clone();
    selector?.children()?.forEach(c => {
      (c as Path)
        .addClass('cursor-pointer')
        .appearance(selectorAppearance.selector)
        .mouseover(function () {
          highlighter.children()?.forEach(c => (c as Path).stroke(selectorAppearance.highlighter(true).stroke));
        })
        .mouseout(function () {
          highlighter.children()?.forEach(c => (c as Path).stroke(selectorAppearance.highlighter(false).stroke));
        });

      c.click(() => onClick(pj.connector));
    });

    group.add(highlighter).add(selector);
  });

  group.rect(line.length, line.width).move(0, 0).fill('none').stroke({ color: 'transparent', width: 0.00000001 });

  return group;
}

/**
 *
 * @param connector
 */
export function renderPanelsConnector(connector: ClampJoint) {
  const group = new G().attr({ type: 'panelsJoint' });

  const distributePoints = (): number[] => {
    if (connector.workJointLength <= 0 || connector.edgeOffset < 0 || connector.count < 2) {
      throw new Error('Некорректные параметры');
    }

    return _.times(connector.count, i => connector.edgeOffset + i * connector.interval);
  };

  const renderFns = [renderClampType1, renderClampType2];
  const renderFn = renderFns[connector.type];

  const clampPoints = distributePoints();
  _.times(connector.count, i => {
    group.add(renderFn(connector).cx(clampPoints[i]));
  });

  return group;
}

/**
 *
 * @param joints
 */
export function renderLinesJoints(joints: CountertopLinesJoint[]) {
  return _.compact(joints.map(j => renderLinesJoint(j)));
}

/**
 *
 * @param joint
 */
export function renderLinesJoint(joint: CountertopLinesJoint): G | null {
  const group = new G().attr({ type: 'linesJoint', male: joint.male.uid, female: joint.female.uid });

  switch (joint.type) {
    case CountertopLinesJointTypeEnum.Clamp:
      renderClampJoint(joint).addTo(group);
      break;

    case CountertopLinesJointTypeEnum.MasonsMitre:
      renderMasonsMitreJoint(joint).addTo(group);

      break;

    default:
      return null;
  }

  return group;
}

/**
 *
 * @param joint
 * @param onClick
 * @param onConnectorClick
 */
export function renderLinesJointSelector(
  joint: CountertopLinesJoint,
  onClick: VoidFunction,
  onConnectorClick?: VoidFunction
): G {
  const group = new G().attr({ type: 'linesJointSelector', male: joint.male.uid, female: joint.female.uid });

  switch (joint.type) {
    case CountertopLinesJointTypeEnum.None:
      renderNoneLinesJointSelector(joint, group);
      group.click(onClick);
      break;
    case CountertopLinesJointTypeEnum.Clamp:
      renderLinesClampJointSelector(joint, group, onClick, onConnectorClick);
      break;

    case CountertopLinesJointTypeEnum.MasonsMitre:
      renderMasonsMitreJointSelector(joint, group, onClick, onConnectorClick);
      break;
  }

  return group;
}

/**
 *
 * @param joint
 * @param group
 * @param onClick
 * @param onConnectorClick
 */
function renderLinesClampJointSelector(
  joint: CountertopLinesJoint,
  group: G,
  onClick: VoidFunction,
  onConnectorClick?: VoidFunction
): G {
  const jointGroup = renderClampJoint(joint);

  const highlighter = (jointGroup.findOne('[type="jointPath"]') as Path).appearance(
    selectorAppearance.highlighter(false)
  );

  const selector = highlighter
    .clone()
    .addClass('cursor-pointer')
    .appearance(selectorAppearance.selector)
    .mouseover(function () {
      highlighter.stroke(selectorAppearance.highlighter(true).stroke);
    })
    .mouseout(function () {
      highlighter.stroke(selectorAppearance.highlighter(false).stroke);
    });

  selector.click(onClick);

  const connector = renderLinesClampJointConnectorsSelector(jointGroup, onConnectorClick);

  return group.add(highlighter).add(selector).add(connector?.connectorHighlighters).add(connector?.connectorSelectors);
}

/**
 *
 * @param jointGroup
 * @param onConnectorClick
 */
function renderLinesClampJointConnectorsSelector(
  jointGroup: G,
  onConnectorClick?: VoidFunction
): { connectorHighlighters: G; connectorSelectors: G } {
  const connectorHighlighters = jointGroup.findOne('[type="connectors"]') as G;

  if (!connectorHighlighters) {
    return;
  }

  connectorHighlighters?.children().forEach(c => (c as Path).appearance(selectorAppearance.highlighter(false)));

  const connectorSelectors = connectorHighlighters.clone();
  connectorSelectors?.children().forEach(c => {
    (c as Path)
      .addClass('cursor-pointer')
      .appearance(selectorAppearance.selector)
      .mouseover(function () {
        connectorHighlighters.children().forEach(c => (c as Path).stroke(selectorAppearance.highlighter(true).stroke));
      })
      .mouseout(function () {
        connectorHighlighters.children().forEach(c => (c as Path).stroke(selectorAppearance.highlighter(false).stroke));
      });

    c.click(onConnectorClick);
  });

  return { connectorHighlighters, connectorSelectors };
}

/**
 *
 * @param joint
 * @param group
 * @param onClick
 * @param onConnectorClick
 */
function renderMasonsMitreJointSelector(
  joint: CountertopLinesJoint,
  group: G,
  onClick: VoidFunction,
  onConnectorClick?: VoidFunction
): G {
  const jointGroup = renderMasonsMitreJoint(joint);

  const highlighter = (jointGroup.findOne('[type="jointPath"]') as Path).appearance(
    selectorAppearance.highlighter(false)
  );

  const selector = highlighter
    .clone()
    .addClass('cursor-pointer')
    .appearance(selectorAppearance.selector)
    .mouseover(function () {
      highlighter.stroke(selectorAppearance.highlighter(true).stroke);
    })
    .mouseout(function () {
      highlighter.stroke(selectorAppearance.highlighter(false).stroke);
    });

  selector.click(onClick);

  const connector = renderLinesClampJointConnectorsSelector(jointGroup, onConnectorClick);

  const rect = (jointGroup.findOne('rect') as Shape) ?? undefined;

  return group
    .add(rect)
    .add(highlighter)
    .add(selector)
    .add(connector?.connectorHighlighters)
    .add(connector?.connectorSelectors);
}

/**
 *
 * @param joint
 * @param group
 */
function renderNoneLinesJointSelector(joint: CountertopLinesJoint, group: G): G {
  const highlighter = renderButtLinesJoint(joint.male.width).appearance(selectorAppearance.highlighter(false));

  const selector = highlighter
    .clone()
    .addClass('cursor-pointer')
    .fill(selectorAppearance.selector.fill)
    .stroke(selectorAppearance.selector.stroke)
    .mouseover(function () {
      highlighter.stroke(selectorAppearance.highlighter(true).stroke);
    })
    .mouseout(function () {
      highlighter.stroke(selectorAppearance.highlighter(false).stroke);
    });

  group.add(highlighter).add(selector);

  highlighter.y(selector.cy());

  return group;
}

/**
 *
 * @param length
 */
function renderButtLinesJoint(length: number): Line {
  return renderSideHighlight(length, lineAppearance.stroke.color);
}

/**
 *
 * @param joint
 */
function renderMasonsMitreJoint(joint: CountertopLinesJoint): G {
  const group = new G().attr({ type: 'masonsMitre' });

  const jointLength = joint.male.width;

  const { allowance, radius: R } = joint.params;

  const inclineDeg = 135;
  const bisectorDeg = inclineDeg / 2;
  const roundSideLength = R / Math.tan(deg2rad(bisectorDeg));
  const projectionLengthToAxis = Math.cos(deg2rad(45)) * roundSideLength;

  const path = group
    .path(
      `M0 ${allowance * 2} v${-allowance} L${
        allowance - projectionLengthToAxis
      } ${projectionLengthToAxis} A${R} ${R} 0 0 1 ${allowance + roundSideLength} 0 H${jointLength} V${allowance * 2}`
    )
    .appearance(lineAppearance)
    .attr({ type: 'jointPath' })
    .nonScalingStroke();

  if (joint.connector) {
    const frame = group
      .rect(
        jointLength,
        Math.max(
          allowance * 2,
          joint.connector.l + joint.connector.d * 2,
          joint.connector.l2 + joint.connector.b * 2 + joint.connector.d * 2
        ) + allowance
      )
      .fill('transparent')
      .stroke('none');
    frame.dy(-frame.bbox().h / 2);

    group.add(renderClamps(joint).dy(-allowance));
    path.dy(-allowance);
  }

  return group;
}

/**
 *
 * @param joint
 */
function renderClampJoint(joint: CountertopLinesJoint): G {
  const group = new G().attr({ type: 'clampJoint' });

  const jointLength = joint.male.width;

  if (joint.connector) {
    group.add(renderClamps(joint));
  }

  group
    .line([
      [0, 0],
      [jointLength, 0],
    ])
    .appearance(lineAppearance)
    .attr({ type: 'jointPath' });

  return group;
}

/**
 *
 * @param joint
 */
function renderClamps(joint: CountertopLinesJoint): G {
  const group = new G().attr({ type: 'connectors' });

  const distributePoints = (): number[] => {
    if (joint.connector.workJointLength <= 0 || joint.connector.edgeOffset < 0 || joint.connector.count < 2) {
      throw new Error('Некорректные параметры');
    }

    return _.times(
      joint.connector.count,
      i =>
        joint.connector.edgeOffset +
        i * joint.connector.interval +
        (joint.type === CountertopLinesJointTypeEnum.MasonsMitre ? joint.params.allowance : 0)
    );
  };

  const renderFns = [renderClampType1, renderClampType2];
  const renderFn = renderFns[joint.connector.type];

  const clampPoints = distributePoints();
  _.times(joint.connector.count, i => {
    group.add(renderFn(joint.connector).cx(clampPoints[i]));
  });

  return group;
}

/**
 *
 * @param connector
 */
function renderClampType1(connector: ClampJoint): Path {
  const r = connector.d / 2;
  const cOffset = Math.sqrt(r ** 2 - (connector.b / 2) ** 2);
  const dcyOffset = connector.l / 2;

  return new Path()
    .plot(
      `M ${connector.b / 2} ${-dcyOffset + cOffset} a ${r} ${r} 0 1 0 ${-connector.b} ${0} V ${
        dcyOffset - cOffset
      } a ${r} ${r} 0 1 0 ${connector.b} ${0} Z`
    )
    .appearance(clampAppearance)
    .nonScalingStroke();
}

/**
 *
 * @param connector
 */
function renderClampType2(connector: ClampJoint): Path {
  const r = connector.d / 2;
  const r2 = connector.b / 2;
  const cOffset = Math.sqrt(r ** 2 - (connector.b / 2) ** 2);
  const dcyOffset = connector.l / 2;
  const dcy2Offset = connector.l2 / 2;

  return new Path()
    .plot(
      `M ${connector.b / 2} ${-dcyOffset + cOffset} a ${r} ${r} 0 0 0 0 ${
        -cOffset * 2
      } V ${-dcy2Offset} a ${r2} ${r2} 0 1 0 ${-connector.b} 0 V ${-(dcyOffset + cOffset)} a ${r} ${r} 0 0 0 0 ${
        cOffset * 2
      } V ${dcyOffset - cOffset} a ${r} ${r} 0 0 0 0 ${cOffset * 2} V ${dcy2Offset} a ${r2} ${r2} 0 1 0 ${
        connector.b
      } 0 V ${dcyOffset + cOffset} a ${r} ${r} 0 0 0 0 ${-cOffset * 2} Z`
    )
    .appearance(clampAppearance)
    .nonScalingStroke();
}

/**
 *
 * @param angleProcessing
 */
function renderAngleCutted(angleProcessing: CountertopAngleProcessingBase): G {
  const group = new G().attr({ angleName: angleProcessing.angleNumber });

  let xCut = 0;
  let yCut = 0;

  if (angleProcessing.parameters instanceof Chamfer) {
    xCut = yCut = angleProcessing.parameters.length;
  }

  if (angleProcessing.parameters instanceof Cut) {
    xCut = angleProcessing.parameters.x;
    yCut = angleProcessing.parameters.y;
  }

  group
    .polyline([
      [-xCut, yCut],
      [0, yCut],
      [xCut, 0],
      [xCut, -yCut],
    ])
    .stroke({ ...lineAppearance.stroke, color: lineAppearance.fill.color })
    .fill(lineAppearance.fill)
    .nonScalingStroke();

  getAngleCutContour(xCut, yCut).addTo(group);

  return group;
}

/**
 *
 * @param xLength
 * @param yLength
 */
function getAngleCutContour(xLength: number, yLength): Line {
  return new Line().plot(0, yLength, xLength, 0).stroke(lineAppearance.stroke).fill('none').nonScalingStroke();
}

/**
 *
 * @param angleProcessing
 */
function renderAngleInsideCutting(angleProcessing: CountertopAngleProcessingBase): G {
  const group = new G().attr({ angleName: angleProcessing.angleNumber });

  if (!(angleProcessing.parameters instanceof CutInside)) {
    return group;
  }

  const points = {
    zeroPoint1: new Point(0, angleProcessing.parameters.y),
    zeroPoint2: new Point(angleProcessing.parameters.x, 0),
    roundingPoint1: new Point(
      angleProcessing.parameters.x - angleProcessing.parameters.r,
      angleProcessing.parameters.y
    ),
    roundingPoint2: new Point(
      angleProcessing.parameters.x,
      angleProcessing.parameters.y - angleProcessing.parameters.r
    ),
    angleSize: 90,
    angleRadius: angleProcessing.parameters.r,
    anglePoint: new Point(angleProcessing.parameters.x, angleProcessing.parameters.y),
  };

  getAngleInsideCuttingContour(
    points.zeroPoint1,
    points.zeroPoint2,
    points.roundingPoint1,
    points.roundingPoint2,
    points.anglePoint,
    points.angleRadius
  ).addTo(group);

  group
    .path(
      `M${-group.width()} ${points.zeroPoint1.y} L${points.zeroPoint1.toArray().join(' ')} L${points.roundingPoint1
        .toArray()
        .join(' ')} A${angleProcessing.parameters.r} ${angleProcessing.parameters.r} 0 0 0 ${points.roundingPoint2
        .toArray()
        .join(' ')} L${points.zeroPoint2.toArray().join(' ')} L${points.zeroPoint2.x} ${-group.height()}`
    )
    .stroke({ ...lineAppearance.stroke, color: lineAppearance.fill.color })
    .fill(lineAppearance.fill)
    .nonScalingStroke()
    .backward();

  return group;
}

/**
 *
 * @param angleProcessing
 */
function renderAngleInsideCuttingWithAngle(angleProcessing: CountertopAngleProcessingBase): G {
  const group = new G().attr({ angleName: angleProcessing.angleNumber });

  if (!(angleProcessing.parameters instanceof CutInsideWithAngle)) {
    return group;
  }

  const points = getAngleInsideCuttingWithAnglePoints(angleProcessing);

  getAngleInsideCuttingContour(
    points.zeroPoint1,
    points.zeroPoint2,
    points.roundingPoint1,
    points.roundingPoint2,
    points.anglePoint,
    points.angleRadius
  ).addTo(group);

  group
    .path(
      `M${-group.width()} ${points.zeroPoint1.y} L${points.zeroPoint1.toArray().join(' ')} L${points.roundingPoint1
        .toArray()
        .join(' ')} Q${points.anglePoint.toArray().join(' ')} ${points.roundingPoint2
        .toArray()
        .join(' ')} L${points.zeroPoint2.toArray().join(' ')} L${points.zeroPoint2.x} ${-group.height()}`
    )
    .stroke({ ...lineAppearance.stroke, color: lineAppearance.fill.color })
    .fill(lineAppearance.fill)
    .nonScalingStroke()
    .backward();

  return group;
}

/**
 *
 * @param angleProcessing
 */
function getAngleInsideCuttingWithAnglePoints(angleProcessing: CountertopAngleProcessingBase) {
  if (!(angleProcessing.parameters instanceof CutInsideWithAngle)) {
    throw new Error('angleProcessing params must be CutInsideWithAngle');
  }

  const anglePoint = findIntersectionPoint(
    90 - angleProcessing.parameters.yA,
    0,
    angleProcessing.parameters.y,
    angleProcessing.parameters.xA,
    angleProcessing.parameters.x,
    0
  );

  const { point1: roundingPoint1, point2: roundingPoint2 } = getCutInsightRadiusPoints(
    anglePoint,
    angleProcessing.parameters.r
  );

  return {
    roundingPoint1,
    roundingPoint2,
    angleSize: anglePoint.angle,
    angleRadius: angleProcessing.parameters.r,
    anglePoint: anglePoint.point,
    zeroPoint1: anglePoint.zeroPoint1,
    zeroPoint2: anglePoint.zeroPoint2,
  };
}

/**
 *
 * @param zeroPoint1
 * @param zeroPoint2
 * @param roundingPoint1
 * @param roundingPoint2
 * @param anglePoint
 * @param roundingRadius
 */
function getAngleInsideCuttingContour(
  zeroPoint1: Point,
  zeroPoint2: Point,
  roundingPoint1: Point,
  roundingPoint2: Point,
  anglePoint: Point,
  roundingRadius: number
): Path {
  return new Path()
    .plot(
      `M${zeroPoint1.toArray().join(' ')} L${roundingPoint1.toArray().join(' ')} Q${anglePoint
        .toArray()
        .join(' ')} ${roundingPoint2.toArray().join(' ')} L${zeroPoint2.toArray().join(' ')}`
    )
    .stroke(lineAppearance.stroke)
    .fill('none')
    .nonScalingStroke();
}

/**
 *
 * @param angleProcessing
 */
function renderAngleRounding(angleProcessing: CountertopAngleProcessingBase): G {
  const group = new G().attr({ angleName: angleProcessing.angleNumber });

  if (!(angleProcessing.parameters instanceof Rounding)) {
    return group;
  }

  const r = angleProcessing.parameters.r;
  const processing = group
    .path(`M0 0 v${r} a${r},${r} 0 0,0 -${r},${r} h-${r} z`)
    .stroke('none')
    .fill(lineAppearance.fill)
    .nonScalingStroke();
  group.add(processing);

  group.add(getAngleRoundingContour(r));

  return group;
}

/**
 *
 * @param r
 */
function getAngleRoundingContour(r: number): Path {
  return new Path()
    .plot(`M0 ${r} a${r},${r} 0 0,0 -${r},${r}`)
    .stroke(lineAppearance.stroke)
    .fill('none')
    .nonScalingStroke();
}

/**
 *
 * @param angleProcessing
 */
function renderAngleInsideRounding(angleProcessing: CountertopAngleProcessingBase): G {
  const group = new G().attr({ angleName: angleProcessing.angleNumber });

  if (!(angleProcessing.parameters instanceof RoundingInside)) {
    return group;
  }

  const r = angleProcessing.parameters.r;
  group
    .circle(r * 2)
    .nonScalingStroke()
    .stroke({ ...lineAppearance.stroke, color: lineAppearance.fill.color })
    .fill(lineAppearance.fill);

  getAngleInsideRoundingContour(r).addTo(group);

  return group;
}

/**
 *
 * @param r
 */
function getAngleInsideRoundingContour(r: number): Path {
  return new Path()
    .plot(`M${r * 2} ${r} A${r},${r} 0 0 1 ${r},${r * 2}`)
    .stroke(lineAppearance.stroke)
    .fill('none')
    .nonScalingStroke();
}

/**
 * Функция, которая создает группу и добавляет в нее элементы из массива.
 * @param elements - Массив элементов для добавления в группу.
 * @returns - Созданная группа, содержащая элементы из массива.
 */
export function createGroupWithElements(elements: SvgElement[]): G {
  const group = new G();

  elements.forEach(element => group.add(element));

  return group;
}

/**
 *
 * @param anglesProcessing
 * @param lineGroups
 * @param draw
 */
export function maskAnglesProcessing(anglesProcessing: G, lineGroups: G[], draw: Svg): G {
  const mask = draw.mask();
  const maskGroup = createGroupWithElements(lineGroups).clone().stroke('none');
  maskGroup.children().forEach(l => {
    l.scale(1.1, 1.1, l.cx(), l.cy());
  });

  return anglesProcessing.maskWith(mask.add(maskGroup));
}

/**
 *
 * @param draw
 * @param color
 * @param textureSettings
 */
export function addTexturePattern(
  draw: Svg,
  color: string,
  textureSettings: TextureSettings & { texturePath: string }
): Promise<Pattern> {
  const createSVGPattern = (
    shape: Shape | Container,
    width: NumberAlias,
    height: NumberAlias,
    offsetX = 0,
    offsetY = 0
  ): Pattern => {
    return draw
      .defs()
      .pattern(+width, +height, function (add) {
        add.add(shape);
      })
      .x(offsetX)
      .y(offsetY);
  };

  return new Promise(resolve => {
    if (color) {
      resolve(createSVGPattern(new Rect().size(100, 100).fill(color), 100, 100));
    } else if (textureSettings.texturePath && !textureSettings.stretch) {
      const width = textureSettings.step.x;
      const height = textureSettings.step.y;

      const imgs = [
        new Image({ preserveAspectRatio: 'none', href: textureSettings.texturePath }).size(width, height).scale(-1, 1),
        new Image({ preserveAspectRatio: 'none', href: textureSettings.texturePath }).size(width, height).dx(width),
        new Image({ preserveAspectRatio: 'none', href: textureSettings.texturePath })
          .size(width, height)
          .dy(height)
          .scale(-1, -1),
        new Image({ preserveAspectRatio: 'none', href: textureSettings.texturePath })
          .size(width, height)
          .dmove(width, height)
          .scale(1, -1),
      ];

      const pattern = createSVGPattern(
        createGroupWithElements(imgs),
        width * 2,
        height * 2,
        textureSettings.offset.y,
        textureSettings.offset.x
      ).rotate(90 + textureSettings.rotationDegree);

      resolve(pattern);
    } else if (textureSettings.texturePath && textureSettings.stretch) {
      const rotation = Math.round(textureSettings.rotationDegree / 90) * 90;

      const img = new Image({ preserveAspectRatio: 'none', href: textureSettings.texturePath })
        .size(1, 1)
        .scale(textureSettings.mirror ? -1 : 1)
        .rotate(90 + rotation);
      const pattern = createSVGPattern(img, 1, 1).attr({
        patternContentUnits: 'objectBoundingBox',
        patternUnits: 'objectBoundingBox',
      });

      resolve(pattern);
    }
  });
}

/**
 *
 * @param lines
 * @param onClick
 */
export function renderPanelSelectors(lines: CountertopLine[], onClick: (rect: Rect, group: G) => void): G[] {
  return lines.map(l => renderPanelSelector(l, onClick));
}

/**
 *
 * @param line
 * @param onClick
 */
export function renderPanelSelector(line: CountertopLine, onClick: (rect: Rect, group: G) => void): G {
  const group = new G().attr({ lineId: line.uid, type: 'panelSelectors' });

  let panelHeights = 0;
  line.panels.forEach(p => {
    group
      .rect(p.length, p.width)
      .move(panelHeights, 0)
      .stroke('none')
      .fill('transparent')
      .attr({ type: 'panelSelector', panelId: p.uid })
      .click(function () {
        onClick(this, group);
      });

    const x = (panelHeights += p.length);
  });

  return group;
}

/**
 *
 * @param rect
 * @param scaled
 * @param commonWidth
 * @param commonHeight
 * @param bothTextSizeDirection
 * @param showSides
 * @param onSizeTextClick
 */
export function renderRectSizes(
  rect: Rect,
  scaled: number,
  commonWidth: number,
  commonHeight: number,
  bothTextSizeDirection: 'ltr' | 'rtl',
  showSides?: ('left' | 'top' | 'right' | 'bottom')[],
  onSizeTextClick?: (text: Text, group: G) => void
): G[] {
  showSides = showSides ?? ['left', 'top', 'right', 'bottom'];

  const sides = showSides.map(s => ({
    length: s === 'top' || s === 'bottom' ? rect.width() : rect.height(),
    side: s,
    dimension: (s === 'top' || s === 'bottom' ? 'height' : 'width') as 'height' | 'width',
    position: (s === 'left' || s === 'bottom' ? 'bottom' : 'top') as 'bottom' | 'top',
  }));

  return sides.map(v => {
    const size = renderSize(+v.length, scaled, commonWidth, commonHeight, v.position, onSizeTextClick);

    size.attr({ side: v.dimension });

    switch (v.side) {
      case 'top':
        size.y(+rect.y()).dy(-size.height());
        size.x(rect.x());
        break;

      case 'left':
        {
          size.y(rect.y()).dy(-size.height());
          size.x(rect.x());

          size.rotate(-90, +rect.x(), +rect.y()).dx(-rect.height());

          if (bothTextSizeDirection === 'rtl') {
            size.rotate(180);
          }
        }
        break;

      case 'bottom':
        size.y(rect.y()).dy(rect.height());
        size.x(rect.x());
        break;

      case 'right':
        {
          size.y(rect.y());
          size.x(rect.x());

          size.rotate(-90, +rect.x(), +rect.y()).dx(-rect.height()).dy(rect.width());

          if (bothTextSizeDirection === 'rtl') {
            size.rotate(180);
          }
        }
        break;
    }

    return size;
  });
}

/**
 *
 * @param length
 * @param scaled
 * @param commonWidth
 * @param commonHeight
 * @param position
 * @param onSizeTextClick
 */
export function renderSize(
  length: number,
  scaled: number,
  commonWidth: number,
  commonHeight: number,
  position: 'top' | 'bottom' = 'top',
  onSizeTextClick?: (text: Text, group: G) => void
): G {
  const maxDimension = Math.max(commonWidth, commonHeight);
  const antiScale = _.clamp(1 / scaled, 0.5, 3);
  const direction = position === 'top' ? -1 : 1;

  const group = new G().attr({ type: 'size' });

  const sidesHeight = maxDimension * 0.02 * antiScale * direction;
  const offset = sidesHeight / 2;
  const fontSize = Math.abs(sidesHeight / 2);
  const arrowLength = fontSize;
  const arrowWidth = fontSize / 1.8;

  group.line(0, 0, 0, sidesHeight).stroke(sizeAppearance.stroke).nonScalingStroke();
  group.line(length, 0, length, sidesHeight).stroke(sizeAppearance.stroke).nonScalingStroke();
  const lengthLine = group.line(0, offset, length, offset).stroke(sizeAppearance.stroke).nonScalingStroke();

  group.add(renderArrow([0, offset], arrowLength, arrowWidth));
  group.add(renderArrow([0, offset], arrowLength, arrowWidth).flip('x', length / 2));

  const text = group
    .text(length.toString())
    .font({ size: `${fontSize}px`, fill: sizeAppearance.fill.color })
    .center(lengthLine.cx(), lengthLine.cy())
    .addClass('cursor-pointer')
    .click(function (e) {
      onSizeTextClick(this, group);
    });

  lengthLine.plot(0, offset, +text.bbox().x - fontSize / 1.6, offset);
  group
    .line(text.bbox().x2 + fontSize / 1.6, offset, length, offset)
    .stroke(sizeAppearance.stroke)
    .nonScalingStroke();

  return group;
}

/**
 *
 * @param angleN
 * @param angleProcessing
 * @param defaultSize
 * @param onClick
 */
export function renderAngleSelector(
  angleN: number,
  angleProcessing: CountertopAngleProcessingBase,
  defaultSize: number,
  onClick: (angleNumber: number, angleProcessing: CountertopAngleProcessingBase) => void
): G {
  const group = new G().attr({ type: 'angleSelector' }).attr({ angleName: angleN });

  const highlighter: Shape = renderAngleHighlight(angleProcessing, defaultSize);

  highlighter.appearance(selectorAppearance.highlighter(false));

  const selector = highlighter
    .clone()
    .appearance(selectorAppearance.selector)
    .addClass('cursor-pointer')
    .click(() => {
      onClick(angleN, angleProcessing);
    });

  selector
    .mouseover(function () {
      highlighter.stroke(selectorAppearance.highlighter(true).stroke);
    })
    .mouseout(function () {
      highlighter.stroke(selectorAppearance.highlighter(false).stroke);
    });

  const backdropRect = group
    .rect(+highlighter.width() * 2, +highlighter.height() * 2)
    .fill('none')
    .stroke({ color: 'transparent', width: 0.000001 })
    .center(0, 0);

  highlighter.move(backdropRect.cx(), backdropRect.cy());
  selector.move(backdropRect.cx(), backdropRect.cy());

  group.add(highlighter);
  group.add(selector);

  return group;
}

/**
 *
 * @param length
 */
export function renderPostforming(length: number): Line {
  return renderSideHighlight(length, '#7700e9');
}

/**
 *
 * @param countertop
 * @param line
 * @param panel
 * @param scaled
 * @param onPanelsCountChange
 * @param options
 * @param options.panelSplit
 * @param options.panelRemoving
 */
export function getPanelMenu(
  countertop: Countertop,
  line: CountertopLine,
  panel: CountertopPanel,
  scaled: number,
  onPanelsCountChange: (
    countertop: Countertop,
    line: CountertopLine,
    panel: CountertopPanel,
    mode: 'add' | 'delete'
  ) => void,
  options?: { panelSplit: boolean; panelRemoving: boolean }
): ForeignObject | null {
  const antiScale = 1 / scaled;

  const createButton = (label: string, onClick: (e: MouseEvent) => void) => {
    const button = document.createElement('button');
    button.setAttribute('class', 'w-full flex-auto hover:bg-gray-200');
    button.setAttribute('style', `font-size: ${80 * antiScale}%; padding: 5%`);
    button.innerText = label;
    button.addEventListener('click', onClick);

    return button;
  };

  if (options?.panelSplit || options?.panelRemoving) {
    const splitBtn =
      options?.panelSplit &&
      createButton('Разделить', () => {
        onPanelsCountChange(countertop, line, panel, 'add');
      });

    const removeBtn =
      options?.panelRemoving &&
      createButton('Удалить', () => {
        onPanelsCountChange(countertop, line, panel, 'delete');
      });

    const wrapper = document.createElement('div');
    wrapper.setAttribute('style', `border-width: ${1 * antiScale}px; border-radius: ${5 * antiScale}px`);
    wrapper.setAttribute('class', 'w-full h-full bg-white overflow-hidden flex flex-col border-inherit divide-y');

    splitBtn && wrapper.appendChild(splitBtn);
    removeBtn && wrapper.appendChild(removeBtn);

    return new ForeignObject({ type: 'actions' }).size(100 * antiScale, 30 * 2 * antiScale).add(SVG(wrapper));
  }

  return null;
}

/**
 *
 * @param countertop
 * @param line
 * @param panel
 * @param sizeSide
 * @param fontSize
 * @param width
 * @param height
 * @param defaultValue
 * @param onPanelSizeChange
 */
export function getSideSizeEditor(
  countertop: Countertop,
  line: CountertopLine,
  panel: CountertopPanel,
  sizeSide: 'width' | 'height',
  fontSize: string,
  width: number,
  height: number,
  defaultValue: number,
  onPanelSizeChange: (
    countertop: Countertop,
    line: CountertopLine,
    panel: CountertopPanel,
    side: 'height' | 'width',
    value: number,
    error?: string
  ) => void
): ForeignObject {
  const panelIndex = line?.panels.findIndex(p => p.uid === panel.uid);
  const isFirstPanel = panelIndex == 0;
  const isLastPanel = panelIndex === line?.panels.length - 1;

  const directions = _.compact([
    !isLastPanel && { icon: '🠊', direction: PanelLengthChangeDirection.Right },
    !isFirstPanel && { icon: '🠈', direction: PanelLengthChangeDirection.Left },
    !isFirstPanel && !isLastPanel && { icon: '⬌', direction: PanelLengthChangeDirection.LeftRight },
  ]);

  let button = null;
  if (sizeSide === 'height' && directions.length) {
    button = document.createElement('button');
    button.setAttribute('type', 'button');
    button.setAttribute('etype', 'sizeChangeDirectionBtn');
    button.setAttribute('title', $localize`Нажмите для изменения направления изменения размера`);
    button.classList.add(
      'aspect-square',
      'h-full',
      'w-auto',
      'bg-blue-500',
      'hover:bg-blue-700',
      'text-white',
      'font-bold',
      'rounded'
    );
    button.setAttribute('direction', directions[0].direction.toString());
    button.innerText = directions[0].icon;
    button.style.fontSize = fontSize;
    button.addEventListener('dblclick', event => event.stopPropagation());
    button.addEventListener('click', e => {
      const currentIndex = directions.findIndex(
        d => d.direction === +(e.target as HTMLButtonElement).getAttribute('direction')
      );
      const nextIndex = (currentIndex + 1) % directions.length;

      button.setAttribute('direction', directions[nextIndex].direction.toString());
      button.innerText = directions[nextIndex].icon;
    });
    button.addEventListener('mousedown', function (event) {
      if (event.button === 0) {
        const mouseMoveHandler = function (event) {
          event.stopPropagation();
        };

        document.addEventListener('mousemove', mouseMoveHandler);

        const mouseUpHandler = function () {
          document.removeEventListener('mousemove', mouseMoveHandler);
          document.removeEventListener('mouseup', mouseUpHandler);
        };

        document.addEventListener('mouseup', mouseUpHandler);

        event.stopPropagation();
      }
    });
  }

  const input = document.createElement('input');
  input.setAttribute('type', 'number');
  input.setAttribute('class', 'w-full h-full px-1 outline outline-blue-500 border rounded-md');
  input.style.fontSize = fontSize;
  input.value = defaultValue.toString();
  input.addEventListener('change', (ev: Event): void => {
    const value = +(ev.target as HTMLInputElement).value;

    try {
      if (sizeSide === 'height') {
        const direction: PanelLengthChangeDirection =
          +button?.getAttribute('direction') ?? PanelLengthChangeDirection.None;

        countertop.setPanelLength(value, line.uid, panel.uid, direction);
      } else {
        countertop.setLineWidth(line.uid, value);
      }
    } catch (error) {
      console.log(error);
      if (error instanceof Error) {
        onPanelSizeChange(countertop, line, panel, sizeSide, value, error.message);
      }
    }

    onPanelSizeChange(countertop, line, panel, sizeSide, value);
  });
  input.addEventListener('mousedown', function (event) {
    if (event.button === 0) {
      const mouseMoveHandler = function (event) {
        event.stopPropagation();
      };

      document.addEventListener('mousemove', mouseMoveHandler);

      const mouseUpHandler = function () {
        document.removeEventListener('mousemove', mouseMoveHandler);
        document.removeEventListener('mouseup', mouseUpHandler);
      };

      document.addEventListener('mouseup', mouseUpHandler);

      event.stopPropagation();
    }
  });

  const inputWrapper = document.createElement('div');
  inputWrapper.classList.add('h-full', 'p-1', 'flex', 'flex-row', 'space-x-2');
  inputWrapper.appendChild(input);

  button && inputWrapper.appendChild(button);

  return new ForeignObject({ type: 'sizeSettings' }).size(width, height).add(SVG(inputWrapper));
}

/**
 *
 * @param point
 * @param arrowLength
 * @param arrowWidth
 */
function renderArrow(point: ArrayXY, arrowLength, arrowWidth) {
  return new Polygon()
    .plot([
      ...point,
      point[0] + arrowLength,
      point[1] + arrowWidth / 2,
      point[0] + arrowLength,
      point[1] - arrowWidth / 2,
      ...point,
    ])
    .fill(sizeAppearance.fill)
    .stroke('none')
    .nonScalingStroke();
}

/**
 *
 * @param cutInsideAnglePoint
 * @param cutInsideAnglePoint.point
 * @param cutInsideAnglePoint.zeroPoint1
 * @param cutInsideAnglePoint.zeroPoint2
 * @param cutInsideAnglePoint.angle
 * @param cutInsideRadius
 */
function getCutInsightRadiusPoints(
  cutInsideAnglePoint: {
    point: Point;
    zeroPoint1: Point;
    zeroPoint2: Point;
    angle: number;
  },
  cutInsideRadius: number
): { point1: Point; point2: Point } {
  return {
    point1: interpolatePoint(
      cutInsideAnglePoint.point,
      cutInsideAnglePoint.zeroPoint1,
      getCircleRemotenessFromCornerTop(cutInsideAnglePoint.angle, cutInsideRadius)
    ),
    point2: interpolatePoint(
      cutInsideAnglePoint.point,
      cutInsideAnglePoint.zeroPoint2,
      getCircleRemotenessFromCornerTop(cutInsideAnglePoint.angle, cutInsideRadius)
    ),
  };
}

/**
 *
 * @param angle
 * @param r
 */
function getCircleRemotenessFromCornerTop(angle: number, r: number): number {
  // Расстояние до центра окружности
  const distanceToCircleCenter = r / Math.sin(deg2rad(angle) / 2);
  // Расстояние от вершины угла до пересечения окружности и касательной
  const distanceToCircleIntersection = Math.sqrt(distanceToCircleCenter ** 2 - r ** 2);

  return distanceToCircleIntersection;
}

/**
 * Метод, вычисляющий точку на отрезке AB от точки A на заданное расстояние
 * @param pointA
 * @param pointB
 * @param distance
 */
function interpolatePoint(pointA: Point, pointB: Point, distance: number): Point {
  const deltaX = pointB.x - pointA.x;
  const deltaY = pointB.y - pointA.y;
  const percentage = distance / Math.sqrt(deltaX ** 2 + deltaY ** 2);

  const interpolatedX = pointA.x + deltaX * percentage;
  const interpolatedY = pointA.y + deltaY * percentage;

  return new Point(interpolatedX, interpolatedY);
}

/**
 *
 *Поиск пересечения двух функций y(x)=tan(angle)*(x-xOffset)+yOffset. (y=xk+b)
 * @param angle1 Угол отклонения от оси X в градусах для первой функции. Положительное число — против часовой стрелки, отрицательное — по часовой стрелке
 * @param xOffset1 Смещение по оси X для первой функции
 * @param yOffset1 Смещение по оси Y для первой функции
 * @param angle2 Угол отклонения от оси X в градусах для второй функции. Положительное число — против часовой стрелки, отрицательное — по часовой стрелке
 * @param xOffset2 Смещение по оси X для второй функции
 * @param yOffset2 Смещение по оси Y для второй функции
 * @returns Координаты точки пересечения функций, Если пересечений нет, возвращается NaN
 */
function findIntersectionPoint(
  angle1: number,
  xOffset1: number,
  yOffset1: number,
  angle2: number,
  xOffset2: number,
  yOffset2: number
): { point: Point; zeroPoint1: Point; zeroPoint2: Point; angle: number } {
  // Расчет угловых коэффициентов
  const m1 = Math.tan(deg2rad(angle1));
  const m2 = Math.tan(deg2rad(angle2));

  // Если угол наклона функций одинаков, то они не пересекаются
  if (m1 === m2) {
    return null;
  }

  // Расчет координат точки пересечения
  const x = (yOffset2 - yOffset1 + m1 * xOffset1 - m2 * xOffset2) / (m1 - m2);
  const y = m1 * (x - xOffset1) + yOffset1;

  // Угол образованный прямыми
  let angle = 0;
  if (1 + m1 * m2 !== 0) {
    angle = Math.abs(rad2deg(Math.atan((m1 - m2) / (1 + m1 * m2))));
  } else {
    angle = 90;
  }

  // Координаты начала отрезка от оси до пересечения прямых
  const zeroPoint1 = new Point(xOffset1, yOffset1);
  const zeroPoint2 = new Point(xOffset2, yOffset2);

  const point = new Point(x, y);

  return { point, zeroPoint1, zeroPoint2, angle };
}

const deg2rad = (deg: number): number => deg * (Math.PI / 180);
const rad2deg = (rad: number): number => (rad * 180) / Math.PI;
